import React, { useState, useEffect } from 'react';
import api from '../api';
import { useToast } from '../Toast';

const ROLE_LABEL = { admin: 'Admin', department_head: 'Opiekun działu', employee: 'Pracownik' };
const ROLE_BADGE = { admin: 'badge-admin', department_head: 'badge-department_head', employee: 'badge-employee' };

function flattenDepts(nodes, depth = 0, acc = []) {
  nodes.forEach(n => { acc.push({ ...n, depth }); if (n.children?.length) flattenDepts(n.children, depth + 1, acc); });
  return acc;
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [allDepts, setAllDepts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'employee' });
  const [userDepts, setUserDepts] = useState([]); // dept IDs this user has access to
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    load();
    api.get('/departments').then(r => setAllDepts(flattenDepts(r.data))).catch(() => {});
  }, []);

  async function load() { const r = await api.get('/auth/users'); setUsers(r.data); }

  async function openNew() {
    setEditUser(null);
    setForm({ email: '', name: '', password: '', role: 'employee' });
    setUserDepts([]);
    setShowModal(true);
  }

  async function openEdit(u) {
    setEditUser(u);
    setForm({ email: u.email, name: u.name, password: '', role: u.role });
    // Load current dept access for this user
    const deptAccess = await Promise.all(
      allDepts.map(d => api.get(`/departments/${d.id}/access`).then(r => r.data).catch(() => []))
    );
    const myDepts = allDepts.filter((d, i) => deptAccess[i].some(x => x.id === u.id)).map(d => d.id);
    setUserDepts(myDepts);
    setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      let savedUserId = editUser?.id;
      if (editUser) {
        await api.put(`/auth/users/${editUser.id}`, form);
        toast('Użytkownik zaktualizowany');
      } else {
        const r = await api.post('/auth/register', form);
        savedUserId = r.data.id;
        toast('Użytkownik dodany');
      }

      // Save department access — only for employees (admins and dept_heads see everything already)
      const effectiveRole = form.role;
      if (effectiveRole === 'employee' && savedUserId) {
        // For each department, update access to include or exclude this user
        for (const dept of allDepts) {
          const currentAccess = await api.get(`/departments/${dept.id}/access`).then(r => r.data).catch(() => []);
          const currentIds = currentAccess.map(u => u.id);
          const shouldHaveAccess = userDepts.includes(dept.id);
          const hasAccess = currentIds.includes(savedUserId);

          if (shouldHaveAccess !== hasAccess) {
            let newIds = shouldHaveAccess
              ? [...currentIds, savedUserId]
              : currentIds.filter(id => id !== savedUserId);
            await api.post(`/departments/${dept.id}/access`, { user_ids: newIds });
          }
        }
      }

      setShowModal(false);
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'Błąd', 'error');
    } finally { setSaving(false); }
  }

  function toggleDept(deptId) {
    setUserDepts(prev => prev.includes(deptId) ? prev.filter(d => d !== deptId) : [...prev, deptId]);
  }

  async function toggleActive(u) {
    await api.put(`/auth/users/${u.id}`, { active: !u.active });
    toast(u.active ? 'Dezaktywowano' : 'Aktywowano');
    load();
  }

  const showDeptSection = form.role === 'employee';

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title" style={{ margin: 0 }}>Użytkownicy</h1>
        <button className="btn btn-primary" onClick={openNew}>+ Dodaj użytkownika</button>
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table>
          <thead><tr><th>Imię i nazwisko</th><th>Email</th><th>Rola</th><th>Status</th><th>Dodano</th><th></th></tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><div className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>{u.name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)}</div>{u.name}</div></td>
                <td style={{ color: 'var(--gray-500)' }}>{u.email}</td>
                <td><span className={`badge ${ROLE_BADGE[u.role]}`}>{ROLE_LABEL[u.role]}</span></td>
                <td><span style={{ fontSize: 12.5, color: u.active ? 'var(--success)' : 'var(--gray-400)' }}>{u.active ? '● Aktywny' : '○ Nieaktywny'}</span></td>
                <td style={{ color: 'var(--gray-400)', fontSize: 12.5 }}>{new Date(u.created_at).toLocaleDateString('pl-PL')}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>Edytuj</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => toggleActive(u)}>{u.active ? 'Dezaktywuj' : 'Aktywuj'}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <span className="modal-title">{editUser ? 'Edytuj użytkownika' : 'Nowy użytkownik'}</span>
              <button className="btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Imię i nazwisko *</label>
                  <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required disabled={!!editUser} />
                </div>
                <div className="form-group">
                  <label className="form-label">{editUser ? 'Nowe hasło (puste = bez zmian)' : 'Hasło *'}</label>
                  <input className="form-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required={!editUser} minLength={editUser ? 0 : 6} />
                </div>
                <div className="form-group">
                  <label className="form-label">Rola</label>
                  <select className="form-select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                    <option value="employee">Pracownik</option>
                    <option value="department_head">Opiekun działu</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {showDeptSection && allDepts.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Dostęp do działów</label>
                    <p className="text-muted" style={{ marginBottom: 10 }}>Zaznacz działy, do których pracownik ma mieć dostęp (widzi procedury w tych działach)</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 6, padding: '8px 12px' }}>
                      {allDepts.map(d => (
                        <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', paddingLeft: d.depth * 16 }}>
                          <input type="checkbox" checked={userDepts.includes(d.id)} onChange={() => toggleDept(d.id)} />
                          <span style={{ fontSize: 13.5 }}>{d.depth > 0 ? '↳ ' : ''}{d.name}</span>
                        </label>
                      ))}
                    </div>
                    {allDepts.length === 0 && <p className="text-muted">Brak działów — najpierw dodaj działy w sekcji "Działy"</p>}
                  </div>
                )}

                {!showDeptSection && (
                  <div style={{ background: 'var(--gray-50)', borderRadius: 6, padding: '10px 14px', fontSize: 13, color: 'var(--gray-500)' }}>
                    ℹ️ Admini i Opiekunowie działów mają automatycznie dostęp do wszystkich działów i procedur.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" type="button" onClick={() => setShowModal(false)}>Anuluj</button>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Zapisywanie...' : 'Zapisz'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
